import { addMonths } from '@finsim/model';
import { ChevronRight, Download } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui';
import { cellValue, ledgerColumns, ledgerCsv, ledgerRows } from '../lib/ledger';
import type { CanvasData } from './use-canvas';

/** Years × items table for the focused variant (years expand into months), with CSV export. */
export function Ledger({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const { project, result, focused, focusedResult, valueMode, buckets, dates, fmt } = data;
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  const labels = useMemo(
    () => ({
      overdraft: t('ledger.overdraft'),
      incomeTax: t('tax.incomeTax'),
      netCashFlow: t('ledger.netCashFlow'),
      cash: t('ledger.cashEnd'),
      assets: t('ledger.assetsEnd'),
      liabilities: t('ledger.liabilitiesEnd'),
      netWorth: t('ledger.netWorthEnd'),
    }),
    [t],
  );
  const rows = useMemo(
    () =>
      focused && focusedResult
        ? ledgerRows(project, focused.id, focusedResult, valueMode, labels)
        : [],
    [project, focused, focusedResult, valueMode, labels],
  );
  const columns = useMemo(() => ledgerColumns(buckets, expanded), [buckets, expanded]);

  if (!focused || !focusedResult) return null;

  const exportCsv = (): void => {
    const nominalRows = ledgerRows(project, focused.id, focusedResult, 'nominal', labels);
    const months = Array.from({ length: result.months }, (_, i) =>
      addMonths(result.startYearMonth, i),
    );
    const blob = new Blob([ledgerCsv(nominalRows, months)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name} – ${focused.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleYear = (year: number): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(year)) next.delete(year);
      else next.add(year);
      return next;
    });
  };

  const monthLabel = (d: Date | undefined): string => (d ? (fmt.month(d).split(' ')[0] ?? '') : '');
  const itemGroupRows = new Set(['total', 'balance', 'overdraft']);
  const startsGroup = rows.map(
    (row, i) =>
      !itemGroupRows.has(row.group) && row.parent === undefined && row.group !== rows[i - 1]?.group,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-medium">{t('ledger.title', { name: focused.name })}</h2>
          <p className="text-xs text-text-3">{data.moneyCaption ?? t('ledger.nominalHint')}</p>
        </div>
        <Button size="sm" leadingIcon={<Download />} onClick={exportCsv}>
          {t('ledger.exportCsv')}
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border-subtle bg-bg-1">
        <table className="border-separate border-spacing-0 text-xs">
          <caption className="sr-only">{t('ledger.title', { name: focused.name })}</caption>
          <thead>
            <tr>
              <th
                scope="col"
                className="sticky top-0 left-0 z-20 min-w-48 border-b border-border-subtle bg-bg-1 px-3 py-2 text-left font-medium text-text-3"
              >
                {t('charts.item')}
              </th>
              {buckets.map((b) => {
                const open = expanded.has(b.year);
                return (
                  <th
                    key={b.year}
                    scope="colgroup"
                    colSpan={open ? b.to - b.from + 1 : 1}
                    className="sticky top-0 z-10 border-b border-l border-border-subtle bg-bg-1 px-1 py-1 text-right font-medium"
                  >
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={
                        open
                          ? t('ledger.collapseYear', { year: b.year })
                          : t('ledger.expandYear', { year: b.year })
                      }
                      onClick={() => {
                        toggleYear(b.year);
                      }}
                      className="inline-flex w-full items-center justify-end gap-1 rounded-sm px-2 py-1 text-text-2 outline-none hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus"
                    >
                      <ChevronRight
                        className={cn('size-3 transition-transform', open && 'rotate-90')}
                        aria-hidden
                      />
                      {b.partial ? `${String(b.year)}*` : b.year}
                    </button>
                  </th>
                );
              })}
            </tr>
            {expanded.size > 0 && (
              <tr>
                <th className="sticky left-0 z-20 bg-bg-1" />
                {columns.map((c) => {
                  const monthly =
                    c.from === c.to &&
                    buckets.some((b) => expanded.has(b.year) && c.from >= b.from && c.to <= b.to);
                  return (
                    <th
                      key={c.key}
                      scope="col"
                      className="border-b border-border-subtle bg-bg-1 px-3 py-1 text-right font-normal text-text-3"
                    >
                      {monthly ? monthLabel(dates[c.from]) : ''}
                    </th>
                  );
                })}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const heading = startsGroup[rowIndex];
              const emphasis = row.group === 'total' || row.id === 'netWorth';
              return (
                <Fragment key={row.id}>
                  {heading && (
                    <tr>
                      <th
                        colSpan={columns.length + 1}
                        scope="colgroup"
                        className="sticky left-0 bg-bg-1 px-3 pt-3 pb-1 text-left text-2xs font-medium text-text-3"
                      >
                        {t(`groups.${row.group as 'income'}`)}
                      </th>
                    </tr>
                  )}
                  <tr className={cn('hover:bg-bg-3', row.group === 'total' && 'border-t')}>
                    <th
                      scope="row"
                      className={cn(
                        'sticky left-0 z-10 bg-bg-1 px-3 py-1.5 text-left font-normal whitespace-nowrap text-text-2',
                        emphasis && 'font-medium text-text-1',
                        row.parent !== undefined && 'pl-7 text-text-3',
                        (row.group === 'total' || row.id === 'cash') &&
                          'border-t border-border-subtle',
                      )}
                    >
                      {row.label}
                    </th>
                    {columns.map((c) => {
                      const v = cellValue(row, c);
                      return (
                        <td
                          key={c.key}
                          className={cn(
                            'tabular px-3 py-1.5 text-right whitespace-nowrap',
                            v < 0 && 'text-negative',
                            v === 0 && 'text-text-3',
                            emphasis && 'font-medium',
                            row.parent !== undefined && 'text-text-3',
                            (row.group === 'total' || row.id === 'cash') &&
                              'border-t border-border-subtle',
                          )}
                        >
                          {v === 0 ? '–' : fmt.money(v)}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-text-3">{t('ledger.partialNote')}</p>
    </div>
  );
}
